import { FC, useMemo, useCallback } from 'react'
import { Advisor } from 'components/features/coaching/advisor/types/advisor'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { reviewsByUsernameSelector, requestByUsernameSelector } from 'components/features/coaching/advisorMeeting/selectors/advisorsSelector'
import { AdvisorReview } from 'components/features/coaching/advisorPublicPage/AdvisorReview/AdvisorReview'
import moment from 'moment'
import Button from 'components/features/pfm/Button/Button'
import styles from 'components/features/coaching/advisorPublicPage/AdvisorReviewsBlock/AdvisorReviewsBlock.styl'
import { GetPublicAdvisorReviewsAction } from 'components/features/coaching/advisorStore/actions/publicAdvisorActions'
import { ModulePreset } from 'components/features/coaching/advisorPublicPage/ModulePreset/ModulePreset'
import { ReadMore } from 'components/atoms/ReadMore/ReadMore'

type AdvisorReviewsBlockProps = {
  advisor: Advisor
  onScheduleCall: () => void
}
export const AdvisorReviewsBlock: FC<AdvisorReviewsBlockProps> = (props) => {
  const { advisor, onScheduleCall } = props
  const { username } = advisor
  const withUsername = useMemo(() => ({ username }), [username])
  const reviews = useStoreSelector((state) => reviewsByUsernameSelector(state, withUsername))
  const request = useStoreSelector((state) => requestByUsernameSelector(state, withUsername))

  const dispatch = useStoreDispatch()
  const handleSeeMore = useCallback(() => {
    dispatch(new GetPublicAdvisorReviewsAction({ advisor_username: username }))
  }, [dispatch, username])

  const reviewsChild = useMemo(() => {
    return reviews?.map((item) => (
      <div className={ styles.review } key={ item.uid }>
        <AdvisorReview
          rating={ item.rating }
          date={ moment(item.created).toISOString() }
          name={ item.author.short_name }
          from={ item.author.city_full_name }
        >
          <ReadMore
            wideLimit={ 1200 }
            narrowLimit={ 700 }
            readMoreChild='Read More'
            text={ item.text }
          />
        </AdvisorReview>
      </div>
    )) ?? null
  }, [reviews])

  const seeMoreButton = useMemo(() => {
    if (!request?.next) {
      return null
    }
    return (
      <Button
        fullWidth
        theme='lightBlue'
        onClick={ handleSeeMore }
        isSubmitting={ request.isLoading }
      >
        SEE MORE
      </Button>
    )
  }, [request?.next, handleSeeMore, request?.isLoading])

  if (!reviewsChild || reviewsChild.length === 0) {
    return (
      <ModulePreset title='Reviews'>
        <button onClick={ onScheduleCall } className={ styles.zeroState }>
          <span>Schedule a call</span> and be the first <br />
          to review { advisor.name }.
        </button>
      </ModulePreset>
    )
  }

  return (
    <ModulePreset className={ styles.modulePreset } title='Reviews'>
      { reviewsChild }
      { seeMoreButton }
    </ModulePreset>
  )
}
